//changed due to package upgrade
import React, { useEffect, useRef, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import IntlMessages from '@projectK/helpers/IntlMessages';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import AppTextField from '@projectK/components/AppTextField';
import AppGridContainer from '@projectK/components/AppGridContainer';
import { alpha, Grid, IconButton } from '@mui/material';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import jwtAxios from '@projectK/services/auth/JWT';
import { CloseRounded } from '@mui/icons-material';
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";   // import styles
import CompanyTheme from '@projectK/constants/CompanyTheme';
import AppModalConfirmation from '@projectK/components/AppModalConfirmation';
import PropTypes from 'prop-types';

const validationSchema = yup.object({
  waiver_name: yup
    .string()
    .max(100, <IntlMessages id='validation.waiverNameTooLong'/>)
    .required(<IntlMessages id='validation.wavierNameRequired' />),
  description: yup
    .string()
    .test('max-50kb', <IntlMessages id='validation.descriptionTooLarge'/>, (value) => {
      // If there is no value, consider it valid
      if (!value) return true;
      // Check the byte size of the content
      return new Blob([value]).size <= 51200;
    })
    .required(<IntlMessages id='validation.descriptionRequired' />),
});

const AddWaivers = ({
  isOpen,
  onClose,
  fetchData,
  handleBlur,
  verbiageSettings,
}) => {
  const companyTheme = CompanyTheme();
  const infoViewActionsContext = useInfoViewActionsContext();
  const [openDialog, setOpenDialog] = useState(false);
  const editorRef = useRef(null);
  useEffect(() => {
    if (editorRef?.current) {
      editorRef?.current?.getContent();
    }
  }, []);
  const handleDialogOpen = () => {
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleCloseModalDialog = () => {
    setOpenDialog(false); // to close the dialog
    handleModalClose(); //to close the modal
  };
  const handleModalClose = () => {
    onClose();
  };
  return (
    <>
      <Modal open={isOpen} onClose={handleDialogOpen}>
        <Box
          className='p-popup'
          sx={{
            bgcolor: 'background.paper',
          }}
        >
          <Formik
            initialValues={{
              waiver_name: '',
              description: '',
              require_digital_signature: false,
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting, setFieldError }) => {
              try {
                const requestData = {
                  ...values,
                  require_digital_signature: values.require_digital_signature,
                };

                await jwtAxios.post(`/waiver-settings/`, requestData);

                setSubmitting(false);
                handleCloseModalDialog();
                fetchData();
                infoViewActionsContext.showMessage(
                  'Waiver Settings has been created successfully!',
                );
              } catch (error) {
                if (error.name === 'ValidationError') {
                  error.inner.forEach((validationError) => {
                    setFieldError(
                      validationError.path,
                      validationError.message,
                    );
                  });
                } else {
                  infoViewActionsContext.fetchError(error.response.data.error);
                }
              }
            }}
          >
            {({
              isSubmitting,
              values,
              handleChange,
              setFieldValue,
              touched,
              errors,
            }) => (
              <Form noValidate autoComplete='off'>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography
                      variant='h4'
                      style={{
                        marginTop: '10px',
                        color: companyTheme
                          ? companyTheme.primary.main
                          : 'text.primary',
                      }}
                    >
                      Add {verbiageSettings?.waiver_name || 'Waiver'}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      size='small'
                      sx={{ fontWeight: 'bold', color: '#000000' }}
                      className='icon-btn'
                      onClick={handleDialogOpen}
                    >
                      <CloseRounded />
                    </IconButton>
                  </Box>
                </Box>
                <AppGridContainer sx={{ mt: 2 }}>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <Box
                      sx={{
                        border: '1px solid #EAECF0',
                        p: 6,
                        borderRadius: 3,
                        boxShadow:
                          '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                      }}
                    >
                      <AppGridContainer>
                        <Grid size={{ xs: 12, md: 12 }}>
                          <AppTextField
                            name='waiver_name'
                            variant='outlined'
                            sx={{
                              width: '100%',
                              my: 2,
                            }}
                            label={`${
                              verbiageSettings?.waiver_name || 'Waiver'
                            } Name`}
                            inputProps={{ maxLength: 101 }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12}} style={{ marginBottom: '40px' }}>
                          <Typography
                            variant='h5'
                            style={{ marginBottom: '10px' }}
                          >
                            Description
                          </Typography>
                          <ReactQuill
                            className='desc-box'
                            ref={editorRef}
                            value={values.description}
                            name='description'
                            onChange={(content, delta, source, editor) => {
                              setFieldValue('description', content);
                            }}
                            modules={{
                              toolbar: [
                                [
                                  { header: '1' },
                                  { header: '2' },
                                  { font: [] },
                                ],
                                [{ list: 'ordered' }, { list: 'bullet' }],
                                [
                                  'bold',
                                  'italic',
                                  'underline',
                                  'strike',
                                  'blockquote',
                                ],
                                [{ align: [] }],
                                ['link', 'image'],
                                ['clean'],
                              ],
                            }}
                            formats={[
                              'header',
                              'font',
                              'list',
                              'bold',
                              'italic',
                              'underline',
                              'strike',
                              'blockquote',
                              'align',
                              'link',
                              'image',
                            ]}
                          />
                          {touched.description && errors.description && (
                            <div
                              className='error'
                              style={{
                                color: '#d32f2f',
                                fontSize: '13px',
                                marginLeft: '13px',
                                marginTop: '6px',
                              }}
                            >
                              {errors.description}
                            </div>
                          )}
                        </Grid>

                        {/* <Grid size={{ xs: 12, md: 12 }}>
                        <FormControlLabel
                          control={
                            <Field
                              component={Checkbox}
                              type='checkbox'
                              name='require_digital_signature'
                              id='require_digital_signature'
                              onChange={handleChange}
                              checked={values.require_digital_signature}
                            />
                          }
                          label='Require Digital Signature'
                        />
                      </Grid> */}

                        <Grid size={{ xs: 12, md: 12 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Button
                              sx={{
                                position: 'relative',
                                minWidth: 100,
                                backgroundColor: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                                '&:hover': {
                                  backgroundColor: companyTheme
                                    ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                                    : alpha('#2196f3', 0.8),
                                },
                              }}
                              variant='contained'
                              type='submit'
                            >
                              <IntlMessages id='common.save' />
                            </Button>
                            <Button
                              sx={{
                                position: 'relative',
                                minWidth: 100,
                                ml: 2.5,
                                borderColor: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                                '&:hover': {
                                  borderColor: companyTheme
                                    ? alpha(companyTheme.primary.main, 0.8)
                                    : alpha('#2196f3', 0.8),
                                },
                                color: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                              }}
                              variant='outlined'
                              onClick={handleDialogOpen}
                            >
                              <IntlMessages id='common.cancel' />
                            </Button>
                          </Box>
                        </Grid>
                      </AppGridContainer>
                    </Box>
                  </Grid>
                </AppGridContainer>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
      <AppModalConfirmation
        openDialog={openDialog}
        handleCloseDialog={handleCloseDialog}
        handleCloseModalDialog={handleCloseModalDialog}
      />
    </>
  );
};

export default AddWaivers;
AddWaivers.propTypes = {
  isOpen: PropTypes.bool,
  fetchData: PropTypes.func,
  onClose: PropTypes.func,
  handleBlur: PropTypes.func,
  verbiageSettings: PropTypes.object,
};
